//
//  QaProgramChecklistCompletionControl.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the QA Program Checklist completion control.
//

import { SegmentedControl } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { FC } from "react";

import { QaProgramChecklistCompletionState } from "src/api/Tmd.dtos";

/** Supplies the task being set, its current state and whether the control accepts input. */
export interface QaProgramChecklistCompletionControlProps {
    itemId?: number;
    task: string;
    completionState: QaProgramChecklistCompletionState;
    disabled: boolean;
    fullWidth?: boolean;
    onCompletionChange: (
        itemId: number,
        completionState: QaProgramChecklistCompletionState
    ) => Promise<unknown>;
}

/** Presents the three completion states in the order the source workbook's column uses. */
export const qaProgramChecklistCompletionOptions = [
    { value: String(QaProgramChecklistCompletionState.Yes), label: "Yes" },
    { value: String(QaProgramChecklistCompletionState.NotApplicable), label: "N/A" },
    { value: String(QaProgramChecklistCompletionState.No), label: "No" }
];

/**
 * Writes a task's completion state straight from the list, without a drill-down.
 *
 * Postconditions: Failed writes notify and leave the displayed state to the refreshed checklist;
 * the returned promise is not awaited. Tasks without an ID render a disabled control. A fixed
 * width keeps the control aligned down a list; fullWidth suits the narrower pinned panel.
 */
const QaProgramChecklistCompletionControl: FC<QaProgramChecklistCompletionControlProps> = ({
    itemId,
    task,
    completionState,
    disabled,
    fullWidth = false,
    onCompletionChange
}) => (
    <SegmentedControl
        size="xs"
        fullWidth={fullWidth}
        w={fullWidth ? undefined : 196}
        aria-label={`Completion state for ${task}`}
        data={qaProgramChecklistCompletionOptions}
        value={String(completionState)}
        disabled={disabled || !itemId}
        onChange={value =>
            itemId &&
            void onCompletionChange(
                itemId,
                Number(value) as QaProgramChecklistCompletionState
            ).catch(() =>
                notifications.show({
                    color: "error",
                    message: "The checklist status could not be updated."
                })
            )
        }
    />
);

export default QaProgramChecklistCompletionControl;
